import { useAuth } from '@/hooks/auth/use-auth';
import { useGroupSuspense } from '../api/get-group';
import { GroupStatus } from '@/types/api/group';
import { MembershipRole } from '@/types/api/membership';
import { useOnboardingPrefs } from '../stores/onboarding-prefs.store';
import { useOnboardingProgress, withLocalProgress } from '../hooks/use-onboarding-progress';
import { OnboardingTaskStatus } from '@/types/api/group/onboarding-task';
import { OnboardingComplete } from '../component/onboarding/onboarding-complete';
import { OnboardingWaiting } from '../component/onboarding/onboarding-waiting';
import { OnboardingProgress } from '../component/onboarding/onboarding-progress';
import { OnboardingGoal } from '../component/onboarding/onboarding-goal';
import { OnboardingTaskList } from '../component/onboarding/onboarding-task-list';

// The API completes onboarding tasks asynchronously, so keep the group fresh while onboarding
const ONBOARDING_POLL_INTERVAL = 3000;

export function GroupOnboarding() {
  const { context } = useAuth();
  const groupId = context?.groupId!;
  const prefs = useOnboardingPrefs(groupId);
  const progress = useOnboardingProgress(groupId);
  const {
    data: group,
    refetch,
    isRefetching,
  } = useGroupSuspense({
    groupId,
    queryConfig: {
      refetchInterval: (query) =>
        query.state.data?.status === GroupStatus.Onboarding ? ONBOARDING_POLL_INTERVAL : false,
      refetchOnWindowFocus: true,
    },
  });

  const tasks = withLocalProgress(group.onboardingTasks, progress);
  const allCompleted = tasks.every((task) => task.status === OnboardingTaskStatus.Completed);

  if (group.status !== GroupStatus.Onboarding || allCompleted) {
    return <OnboardingComplete group={group} saleId={group.defaultSaleId ?? progress.saleId} />;
  }

  if (!context?.membershipRoles.includes(MembershipRole.Leader)) {
    return <OnboardingWaiting group={group} refetch={refetch} isRefetching={isRefetching} />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-4">
        <OnboardingProgress tasks={tasks} title="Kom igång" />
        <p className="text-sm">Några snabba steg så kan pengarna snart börja rulla in.</p>
        <OnboardingGoal prefs={prefs} className="w-fit text-left" />
      </div>
      <OnboardingTaskList tasks={tasks} groupId={group.id} />
    </div>
  );
}
